import {Box, Button, Typography, useMediaQuery} from '@mui/material';
import React, {useEffect, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import axios from 'axios';
import {API_ENDPOINT} from '../config';
import NotaPrevizualizare from '../components/NotaPrevizualizare';
import PhotoGallery from '../components/PhotoGallery';
import {lucrarePath, membruPath} from '../utils/urlPublic';
import {hideSplash} from '../splash';
import {TitluCuDistribuie} from '../components/DistribuieButton';
import LikeButton from '../components/LikeButton';
import {useAprecieriMele} from '../utils/aprecieri';
import DescriereFormatata from '../components/DescriereFormatata';

// 29.4: pe pagina lucrarii, fotografiile vin in varianta nivelului ei (vezi docs/vizibilitate-lucrari.md).
const NOTA_NIVEL = {
    arhiva: 'Fotografie de arhivă — rezoluție redusă; lucrarea se vede la calitate maximă când este expusă.',
};

// 44.4: fragmentele clare alese de artist (API-ul le trimite doar la teaser, inainte de prima participare).
export const detaliiLucrare = (lucrare) => (lucrare?.nivel === 'teaser' ? (lucrare.poze || []) : [])
    .filter((photo) => photo.detaliu?.poza)
    .map((photo) => ({id: `detaliu-${photo.id}`, poza: photo.detaliu.poza,
        descriere: `Detaliu din lucrare${photo.descriere ? ` — ${photo.descriere}` : ''}`}));

// ADR-0001: /artisti|colectionari/{membru}/lucrari/{slug}. 31.7: adresa veche (slug din istoric, membru mutat
// sau de alt tip) -> API-ul da lucrarea curenta; trecem pe adresa ei canonica (replace).
export default function LucrarePublicPage({type = 'artist'}) {
    const {membru, slug} = useParams();
    // Același prag ca breakpoints.down('sm') și ca regula .artwork-navigation din App.css.
    const mobil = useMediaQuery('(max-width:599.95px)');
    const navigate = useNavigate();
    const [lucrare, setLucrare] = useState(null);
    const [loading, setLoading] = useState(true);
    // Ecranul de încărcare (src/splash.js) dispare când pagina are datele - fără „pâlpâire” la deschidere.
    useEffect(() => {
        if (!loading) hideSplash();
    }, [loading]);
    const [error, setError] = useState(null);
    const apreciat = useAprecieriMele({lucrari: lucrare?.id && lucrare.likes !== null ? [lucrare.id] : []});

    useEffect(() => {
        const controller = new AbortController();
        let mutat = false; // trecem pe adresa canonica: ramane „Se incarca” pana la cererea de acolo
        setLoading(true);
        setError(null);
        axios.get(`${API_ENDPOINT}/public/membri/${membru}/lucrari/${slug}`, {signal: controller.signal})
            .then(({data}) => {
                if (data.artistType === type && data.artistSlug === membru && data.slug === slug) setLucrare(data);
                else { mutat = true; navigate(lucrarePath(data.artistType, data.artistSlug, data.slug), {replace: true}); }
            })
            .catch((err) => {
                if (!controller.signal.aborted) setError(err.response?.status === 404
                    ? 'Lucrare negăsită.' : 'Lucrarea nu poate fi încărcată momentan. Încearcă din nou.');
            })
            .finally(() => { if (!controller.signal.aborted && !mutat) setLoading(false); });
        return () => controller.abort();
    }, [membru, slug, type, navigate]);
    const detalii = detaliiLucrare(lucrare);

    return (
        <Box className="public-detail">
            {loading ? <Typography color="text.secondary" role="status">Se încarcă...</Typography> : error ? (
                <Typography role="alert">{error}</Typography>
            ) : (
                <>
                    <Box component="header" sx={{mb: 3}}>
                        <TitluCuDistribuie titlu={`${lucrare.nume} — ArtaNFT`} text={[lucrare.nume, lucrare.artist].filter(Boolean).join(' — ')}
                            like={<LikeButton tip="lucrari" id={lucrare.id} likes={lucrare.likes} apreciat={apreciat('lucrari', lucrare.id)} nume={lucrare.nume} />}>
                            <Typography component="h1" variant="h4">{lucrare.nume}</Typography>
                        </TitluCuDistribuie>
                        {lucrare.artistSlug ? <Link to={`${membruPath(type, lucrare.artistSlug)}?pagina=${lucrare.navigare?.paginaArtist || 1}`}>{lucrare.artist}</Link> : <Typography>{lucrare.artist}</Typography>}
                    </Box>
                    {/* Pasul 38: pe telefon cele două butoane stau pe același rând, 50% / 50%, cu text scurt. */}
                    <nav className="artwork-navigation" aria-label="Lucrările artistului">
                        {lucrare.navigare?.anterior ? <Button variant="outlined" component={Link} to={lucrarePath(type, membru, lucrare.navigare.anterior)} aria-label="Lucrarea anterioară">&larr; {mobil ? 'Anterioara' : 'Lucrarea anterioară'}</Button> : <Button variant="outlined" disabled aria-label="Lucrarea anterioară">&larr; {mobil ? 'Anterioara' : 'Lucrarea anterioară'}</Button>}
                        {lucrare.navigare?.urmator ? <Button variant="outlined" component={Link} to={lucrarePath(type, membru, lucrare.navigare.urmator)} aria-label="Lucrarea următoare">{mobil ? 'Următoarea' : 'Lucrarea următoare'} &rarr;</Button> : <Button variant="outlined" disabled aria-label="Lucrarea următoare">{mobil ? 'Următoarea' : 'Lucrarea următoare'} &rarr;</Button>}
                    </nav>
                    {lucrare.nivel === 'teaser' && <NotaPrevizualizare expozitie={lucrare.expozitieViitoare}
                        profil={lucrare.artistSlug ? membruPath(type, lucrare.artistSlug) : null} cuDetaliu={detalii.length > 0} />}
                    <Box className="artwork-detail-grid">
                        <Box>
                            {/* 44.4: fragmentul clar e imaginea principala (marirea arata doar fragmentul);
                                compozitia integrala blurata ramane secundar, cu eticheta ei. */}
                            {detalii.length > 0 ? <>
                                <PhotoGallery key={`${membru}/${slug}/detalii`} photos={detalii} title={`${lucrare.nume} — detaliu`} />
                                <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>Detaliu din lucrare, ales de artist.</Typography>
                                <Box component="section" aria-label="Compoziția integrală, în previzualizare" sx={{mt: 3, maxWidth: 360}}>
                                    <Typography component="h2" variant="subtitle2" sx={{mb: 1}}>Compoziția integrală — previzualizare</Typography>
                                    <PhotoGallery key={`${membru}/${slug}/teaser`} photos={lucrare.poze} title={lucrare.nume} faraMarire />
                                </Box>
                            </> : lucrare.poze?.length > 0 && <PhotoGallery key={`${membru}/${slug}`} photos={lucrare.poze} title={lucrare.nume}
                                faraMarire={lucrare.nivel === 'teaser'} />}
                            {NOTA_NIVEL[lucrare.nivel] && lucrare.poze?.length > 0 && detalii.length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>{NOTA_NIVEL[lucrare.nivel]}</Typography>
                            )}
                        </Box>
                        <aside className="artwork-details">
                            <Typography component="h2" variant="h6" sx={{mb: 2}}>Despre lucrare</Typography>
                            <dl>
                                {lucrare.an != null && <><dt>An</dt><dd>{lucrare.an}</dd></>}
                                {lucrare.tehnica && <><dt>Tehnică</dt><dd>{lucrare.tehnica}</dd></>}
                                {lucrare.latime != null && <><dt>Lățime</dt><dd>{lucrare.latime} cm</dd></>}
                                {lucrare.inaltime != null && <><dt>Înălțime</dt><dd>{lucrare.inaltime} cm</dd></>}
                            </dl>
                            {lucrare.an == null && !lucrare.tehnica && lucrare.latime == null && lucrare.inaltime == null && !lucrare.descriere && (
                                <Typography color="text.secondary">Detaliile vor fi adăugate în curând.</Typography>
                            )}
                            {/* Pasul 46.4: descrierea lucrarii (HTML redus, curatat in BE si in DescriereFormatata). */}
                            {lucrare.descriere && <Box sx={{mt: 2, overflowWrap: 'anywhere'}}><DescriereFormatata text={lucrare.descriere} /></Box>}
                        </aside>
                    </Box>
                </>
            )}
        </Box>
    );
}
